// @core/stores/secret/SecretSecretEngineStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { secretApi } from '@core/api';
import type {
  ListEnginesResponse,
  MountEngineRequest,
  SecretEngine,
  SecretEngineResponse,
  TuneEngineRequest,
} from '@core/types/secret';
import type { PageInfo } from '@core/types/pagination';

class SecretSecretEngineStore {
  // List state
  items: SecretEngine[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: SecretEngine | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, SecretEngine>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): SecretEngine | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: SecretEngine | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Get secrets engine
   */
  async GetEngine(engine_id: string): Promise<SecretEngine> {
    // Check cache first
    const cached = this.cache.get(engine_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await secretApi.GetEngine(engine_id);
      const item = response.engine;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List secrets engines
   */
  async ListEngines(params?: { cursor?: string | number | boolean; direction?: string | number | boolean; limit?: string | number | boolean; sort?: string | number | boolean; status?: string | number | boolean; type?: string | number | boolean; }): Promise<ListEnginesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.ListEngines(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Mount secrets engine
   */
  async MountEngine(data: MountEngineRequest): Promise<SecretEngineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.MountEngine(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Tune secrets engine
   */
  async TuneEngine(engine_id: string, data: TuneEngineRequest): Promise<SecretEngineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.TuneEngine(engine_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Unmount secrets engine
   */
  async UnmountEngine(engine_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.UnmountEngine(engine_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const secretSecretEngineStore = new SecretSecretEngineStore();
